import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { config } from '@/lib/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/useToast';
import { QrCode, Plus, Trash2, ExternalLink, Download } from 'lucide-react';
import QRCode from 'qrcode.react';
import { downloadQrPngHighRes, QR_HIGH_RES_PX } from '@/lib/qrHighRes';

function normalizeCode(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+/, '');
}

function normalizeTargetPath(raw) {
  let s = String(raw || '').trim();
  if (!s) return '';
  if (!s.startsWith('/')) s = `/${s}`;
  s = s.split('#')[0].split('?')[0];
  if (s.includes('..')) return '';
  if (/^https?:\/\//i.test(s)) return '';
  return s.slice(0, 512);
}

const CODE_RE = /^qr-[a-z0-9]+(?:-[a-z0-9]+)*$/;

export default function AdminQrRedirectsPanel() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codeInput, setCodeInput] = useState('qr-1');
  const [targetInput, setTargetInput] = useState('/fitzsy');
  const [labelInput, setLabelInput] = useState('');
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const publicBase = useMemo(() => config.getBaseUrl().replace(/\/$/, ''), []);

  useEffect(() => {
    if (!db) {
      setLoading(false);
      return;
    }
    const unsub = onSnapshot(
      collection(db, 'qr_redirects'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => a.id.localeCompare(b.id));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        console.error('qr_redirects listener', err);
        toast({
          title: 'Could not load QR redirects',
          description: err.message,
          variant: 'destructive',
        });
        setLoading(false);
      }
    );
    return () => unsub();
  }, [toast]);

  const saveMapping = async () => {
    if (!db) return;
    const code = normalizeCode(codeInput);
    if (!CODE_RE.test(code)) {
      toast({
        title: 'Invalid code',
        description: 'Use a path like qr-1, qr-2, or qr-banner-a (lowercase, after qr-).',
        variant: 'destructive',
      });
      return;
    }
    const targetPath = normalizeTargetPath(targetInput);
    if (!targetPath || !targetPath.startsWith('/')) {
      toast({
        title: 'Invalid target',
        description: 'Enter a site path only, e.g. /fitzsy or /karaoke/myhost — no full URLs.',
        variant: 'destructive',
      });
      return;
    }
    if (targetPath.replace(/\/$/, '') === `/${code}`) {
      toast({
        title: 'Invalid target',
        description: 'Target cannot be the same as the QR path (would loop).',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'qr_redirects', code), {
        targetPath,
        label: labelInput.trim() || null,
        updated_at: serverTimestamp(),
      });
      toast({ title: 'Saved', description: `${publicBase}/${code} → ${targetPath}` });
      setLabelInput('');
    } catch (e) {
      console.error(e);
      toast({
        title: 'Save failed',
        description: e.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const downloadHighRes = async (rowId) => {
    const url = `${publicBase}/${rowId}`;
    setDownloadingId(rowId);
    try {
      await downloadQrPngHighRes(url, `qr-${rowId}`);
      toast({
        title: 'Downloaded',
        description: `High-resolution PNG (${QR_HIGH_RES_PX}×${QR_HIGH_RES_PX}px, print-ready).`,
      });
    } catch (e) {
      console.error(e);
      toast({ title: 'Download failed', description: e.message, variant: 'destructive' });
    } finally {
      setDownloadingId(null);
    }
  };

  const removeRow = async (id) => {
    if (!db) return;
    if (!window.confirm(`Delete short link /${id}?`)) return;
    try {
      await deleteDoc(doc(db, 'qr_redirects', id));
      toast({ title: 'Removed', description: id });
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <QrCode className="w-5 h-5 text-purple-400" />
            Roll-up & promo QR links
          </CardTitle>
          <CardDescription className="text-gray-400">
            URLs like <span className="text-gray-200">{publicBase}/qr-1</span> redirect instantly (HTTP 302) with no
            intermediate page — swap the target anytime for reuse across artists. Codes must start with{' '}
            <code className="text-purple-300">qr-</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Short code</Label>
              <Input
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                placeholder="qr-1"
                className="bg-gray-900 border-gray-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Target path</Label>
              <Input
                value={targetInput}
                onChange={(e) => setTargetInput(e.target.value)}
                placeholder="/fitzsy"
                className="bg-gray-900 border-gray-600 text-white"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Internal note (optional)</Label>
            <Input
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              placeholder="Winter tour – Fitzsy"
              className="bg-gray-900 border-gray-600 text-white"
            />
          </div>
          <Button
            onClick={saveMapping}
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            {saving ? 'Saving…' : editingCode ? `Update ${editingCode}` : 'Save mapping'}
          </Button>
          {editingCode && (
            <Button
              variant="outline"
              onClick={() => {
                setEditingCode(null);
                setCodeInput('qr-1');
                setTargetInput('/fitzsy');
                setLabelInput('');
              }}
            >
              Cancel edit
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Active links</CardTitle>
          <CardDescription className="text-gray-400">
            Print the short URL or QR below. Unconfigured codes send visitors to the homepage.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-400 text-sm">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-gray-400 text-sm">No mappings yet.</p>
          ) : (
            <div className="space-y-6">
              {rows.map((row) => {
                const url = `${publicBase}/${row.id}`;
                return (
                  <div
                    key={row.id}
                    className="flex flex-col lg:flex-row lg:items-start gap-4 p-4 rounded-lg bg-gray-900/60 border border-gray-700"
                  >
                    <div className="bg-white p-3 rounded-lg shrink-0 self-start">
                      <QRCode value={url} size={120} level="M" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="text-purple-300 text-sm font-mono">{url}</code>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-white inline-flex items-center gap-1 text-sm"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Test
                        </a>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/40"
                          disabled={downloadingId === row.id}
                          onClick={() => downloadHighRes(row.id)}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          {downloadingId === row.id ? 'Preparing…' : 'Download HD PNG'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-blue-600 text-blue-400 hover:bg-blue-900/40"
                          onClick={() => {
                            setEditingCode(row.id);
                            setCodeInput(row.id);
                            setTargetInput(row.targetPath || '');
                            setLabelInput(row.label || '');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          Edit redirect
                        </Button>
                      </div>
                      <p className="text-gray-300 text-sm">
                        → <span className="text-white font-medium">{row.targetPath || '—'}</span>
                      </p>
                      {row.label && (
                        <p className="text-gray-500 text-xs">{row.label}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-600 text-red-400 hover:bg-red-900/40 shrink-0"
                      onClick={() => removeRow(row.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
